import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Compass, UserPlus, ShieldCheck } from "lucide-react";
import { BRAND, WHATSAPP_URL } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const institucionalItems = [
  {
    to: "/manifesto",
    icon: Compass,
    label: "Manifesto licito.guru",
    desc: "Nascemos para vencer a insegurança jurídica e a burocracia em licitações e contratos.",
  },
  {
    to: "/faca-parte",
    icon: UserPlus,
    label: "Faça parte da nossa equipe",
    desc: "Junte-se ao time que está reinventando as contratações públicas.",
  },
  {
    to: "/lgpd",
    icon: ShieldCheck,
    label: "LGPD",
    desc: "Como tratamos e protegemos os dados pessoais em nossa plataforma.",
  },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false); // mobile menu
  const [instOpen, setInstOpen] = useState(false); // desktop megamenu
  const [mobileInstOpen, setMobileInstOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setInstOpen(false);
    setMobileInstOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const scrollHref = (id) => (isHome ? `#${id}` : `/#${id}`);

  const handleInstEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setInstOpen(true);
  };
  const handleInstLeave = () => {
    closeTimer.current = setTimeout(() => setInstOpen(false), 120);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur border-b hairline"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-8 flex items-center justify-between h-[92px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={BRAND.logoUrl} alt="licito.guru" className="h-14 md:h-[60px] w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <a
            href={scrollHref("funcionalidades")}
            data-testid="nav-funcionalidades"
            className="text-[14px] text-[#1A3A61]/75 hover:text-[#2EE59D] font-medium transition-colors"
          >
            Funcionalidades
          </a>
          <a
            href={scrollHref("produto")}
            data-testid="nav-ia-personalizada"
            className="text-[14px] text-[#1A3A61]/75 hover:text-[#2EE59D] font-medium transition-colors"
          >
            IA Personalizada
          </a>

          {/* Institucional dropdown */}
          <div
            className="relative"
            onMouseEnter={handleInstEnter}
            onMouseLeave={handleInstLeave}
          >
            <button
              data-testid="nav-institucional"
              aria-expanded={instOpen}
              onClick={() => setInstOpen((v) => !v)}
              className={`flex items-center gap-1.5 text-[14px] font-medium transition-colors ${
                instOpen ? "text-[#2EE59D]" : "text-[#1A3A61]/75 hover:text-[#2EE59D]"
              }`}
            >
              Institucional
              <motion.span animate={{ rotate: instOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                <ChevronDown size={14} strokeWidth={2.2} />
              </motion.span>
            </button>
          </div>
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-login"
            href="https://ai.licito.guru/login"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex text-[14px] text-[#1A3A61]/75 hover:text-[#2EE59D] font-medium px-3 py-2"
          >
            Entrar
          </a>
          <a
            data-testid="nav-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex btn-primary text-[13px]"
          >
            Testar grátis
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-[#1A3A61]"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Desktop Megamenu */}
      <AnimatePresence>
        {instOpen && (
          <motion.div
            data-testid="institucional-megamenu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="hidden lg:block absolute left-0 right-0 top-full bg-white border-b hairline shadow-lg"
            onMouseEnter={handleInstEnter}
            onMouseLeave={handleInstLeave}
          >
            <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-10">
              <div className="mb-6 border-b hairline pb-4">
                <span className="kicker">Conheça mais sobre a licito.guru</span>
              </div>
              <div className="grid grid-cols-2 gap-x-10 gap-y-8">
                {institucionalItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`inst-item-${item.to.slice(1)}`}
                      className="group flex items-start gap-4"
                    >
                      <span className="mt-0.5 flex-shrink-0 h-11 w-11 flex items-center justify-center bg-[#F1F5FF] rounded-md text-[#1A3A61] group-hover:bg-[#1A3A61] group-hover:text-white transition-colors">
                        <Icon size={20} strokeWidth={1.8} />
                      </span>
                      <div>
                        <p className="text-[15px] font-semibold text-[#1A3A61] group-hover:text-[#00C2D1] transition-colors">
                          {item.label}
                        </p>
                        <p className="mt-1 text-[13.5px] text-[#1A3A61]/65 leading-[1.5] max-w-md">
                          {item.desc}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-t hairline"
          >
            <nav className="flex flex-col px-6 py-6">
              <a
                href={scrollHref("funcionalidades")}
                onClick={() => setOpen(false)}
                className="py-3 text-[#1A3A61] text-lg font-medium border-b hairline"
              >
                Funcionalidades
              </a>
              <a
                href={scrollHref("produto")}
                onClick={() => setOpen(false)}
                className="py-3 text-[#1A3A61] text-lg font-medium border-b hairline"
              >
                IA Personalizada
              </a>
              <button
                onClick={() => setMobileInstOpen((v) => !v)}
                data-testid="mobile-inst-toggle"
                className="py-3 text-[#1A3A61] text-lg font-medium border-b hairline flex items-center justify-between"
              >
                Institucional
                <motion.span animate={{ rotate: mobileInstOpen ? 180 : 0 }}>
                  <ChevronDown size={16} />
                </motion.span>
              </button>
              <AnimatePresence>
                {mobileInstOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden border-b hairline"
                  >
                    <div className="py-3 pl-4 space-y-2">
                      {institucionalItems.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setOpen(false)}
                          className="block py-2 text-[15px] text-[#1A3A61]/80"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <a
                href="https://ai.licito.guru/login"
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 text-[#1A3A61] text-lg font-medium border-b hairline"
              >
                Entrar
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 btn-primary justify-center"
              >
                Testar grátis
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
